const STORAGE_KEY = 'featureVisibility';

const featureVisibilityService = {
    /**
     * Get all visible features from localStorage (populated by sync).
     * Returns array of full feature objects.
     * @returns {Array<{featureKey: string, name: string, displayName: string, description: string, category: string, icon: string, color: string, link: string, searchTerms: string[], roles: string[], sortOrder: number}>}
     */
    getFeatures() {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) return parsed;
            } catch {
                // ignore
            }
        }
        return [];
    },

    /**
     * Get visible feature keys only.
     * @returns {string[]}
     */
    getVisibleFeatureKeys() {
        return this.getFeatures().map(f => f.featureKey);
    },

    /**
     * Backward-compatible: returns feature keys for isVisible() checks.
     * @returns {string[]}
     */
    getVisibleFeatures() {
        return this.getVisibleFeatureKeys();
    },

    /**
     * Check if a feature is visible.
     * Fail-open: if no sync data yet, show everything.
     * @param {string} featureKey
     * @param {string[]|null} visibleFeatures - optional pre-loaded list of keys
     * @returns {boolean}
     */
    isVisible(featureKey, visibleFeatures = null) {
        if (!featureKey) return true;

        const keys = visibleFeatures || this.getVisibleFeatureKeys();

        // Fail-open: if no data synced yet, show everything
        if (!keys || keys.length === 0) {
            return true;
        }

        return keys.includes(featureKey);
    },

    /**
     * Get features grouped by category.
     * @returns {Object<string, Array>}
     */
    getFeaturesByCategory() {
        const features = this.getFeatures();
        const grouped = {};
        for (const f of features) {
            const cat = f.category || 'other';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(f);
        }
        // Sort each category by sortOrder
        for (const cat of Object.keys(grouped)) {
            grouped[cat].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        }
        return grouped;
    },

    /**
     * Get a single feature by key.
     * @param {string} featureKey
     * @returns {Object|null}
     */
    getFeature(featureKey) {
        return this.getFeatures().find(f => f.featureKey === featureKey) || null;
    },
};

export default featureVisibilityService;
